import type { SearchState } from '@wineryxplorer/shared';
import type { ScopePill } from '../../hooks/useSearchScope';
import { SearchScopePill } from '../search/SearchScopePill';
import { GeoPrompt } from './GeoPrompt';
import { RegionSearch } from './RegionSearch';
import type { Region, CityLocation } from './RegionSearch';
import { SavedSearchesMenu } from './SavedSearchesMenu';

interface SearchHeaderProps {
  currentSearchState: SearchState;
  currentVenueCount: number;
  defaultSaveName: string;
  showGeoPrompt: boolean;
  isLocating: boolean;
  onUseMyLocation: () => void;
  onDismissGeoPrompt: () => void;
  scopePill: ScopePill;
  searchText: string;
  onSearchTextChange: (value: string) => void;
  isMapAreaMode: boolean;
  onClearMapArea: () => void;
  selectedRegion: Region | null;
  selectedCity: CityLocation | null;
  onRegionChange: (region: Region | null) => void;
  onCitySearch: (city: CityLocation) => void;
  geoError: string | null;
}

/**
 * Top of the Wineries search page: title + saved searches, the geolocation
 * prompt, the current scope pill, and the What / Where input pair.
 */
export const SearchHeader = ({
  currentSearchState,
  currentVenueCount,
  defaultSaveName,
  showGeoPrompt,
  isLocating,
  onUseMyLocation,
  onDismissGeoPrompt,
  scopePill,
  searchText,
  onSearchTextChange,
  isMapAreaMode,
  onClearMapArea,
  selectedRegion,
  selectedCity,
  onRegionChange,
  onCitySearch,
  geoError,
}: SearchHeaderProps) => (
  <div className="space-y-3">
    <div className="flex items-center justify-between gap-2">
      <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Discover Wineries</h1>
      <SavedSearchesMenu
        currentState={currentSearchState}
        currentVenueCount={currentVenueCount}
        defaultSaveName={defaultSaveName}
      />
    </div>

    {/* Geolocation permission prompt */}
    {showGeoPrompt && (
      <GeoPrompt
        isLocating={isLocating}
        onEnable={onUseMyLocation}
        onDismiss={onDismissGeoPrompt}
      />
    )}

    {scopePill ? (
      <div>
        <SearchScopePill label={scopePill.label} onDismiss={scopePill.onDismiss} />
      </div>
    ) : (
      <div className="text-sm text-gray-600">
        <span>Showing wineries everywhere.</span>{' '}
        <button
          type="button"
          onClick={onUseMyLocation}
          className="text-info-700 underline hover:text-info-900 focus:outline-none focus:ring-2 focus:ring-info-500 rounded"
        >
          Use my location
        </button>
        <span className="text-gray-400"> or pick a region for nearby results.</span>
      </div>
    )}

    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      {/* What field */}
      <div>
        <label
          htmlFor="search-what"
          className="block text-xs font-medium text-gray-500 mb-1"
        >
          What are you looking for?
        </label>
        <div className="relative">
          <input
            id="search-what"
            type="text"
            value={searchText}
            onChange={(e) => onSearchTextChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape' && searchText) onSearchTextChange('');
            }}
            placeholder="Winery name, type..."
            className="w-full border border-gray-300 rounded-lg pl-3 pr-8 py-2 text-sm bg-gray-50 focus:outline-none focus:ring-2 focus:ring-info-500 focus:border-info-500"
          />
          {searchText && (
            <button
              type="button"
              onClick={() => onSearchTextChange('')}
              className="absolute inset-y-0 right-0 flex w-8 items-center justify-center text-gray-400 hover:text-gray-700"
              aria-label="Clear search"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Where field — replaced by Map area pill once bounds are committed */}
      {isMapAreaMode ? (
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">
            Where?
          </label>
          <div className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-gray-50 flex items-center justify-between">
            <span className="text-gray-700">Map area</span>
            <button
              type="button"
              onClick={onClearMapArea}
              className="text-gray-400 hover:text-gray-700"
              aria-label="Clear map area"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      ) : (
        <RegionSearch
          selectedRegion={selectedRegion}
          selectedCity={selectedCity}
          onRegionChange={onRegionChange}
          onCitySearch={onCitySearch}
          onUseMyLocation={onUseMyLocation}
          isLocating={isLocating}
          geoError={geoError}
        />
      )}
    </div>
  </div>
);
